import { Random } from "@mongez/reinforcements";
import { useContext, useEffect, useState } from "react";
import { VidePlayer2PContext } from "../context";
import {
  VideoPlayer2Event,
  VideoPlayer2SetupStartOptions,
  VideoPlayerManager2,
} from "../types";

export function useVideoPlayer2(): VideoPlayerManager2 {
  return (
    useContext(VidePlayer2PContext) ||
    (throwError(
      "VideoPlayer2 hook is called outside of VideoPlayer2 component",
    ) as any)
  );
}

export function useVideoPlayerEvent(
  event: VideoPlayer2Event,
  callback?: (player: VideoPlayerManager2) => void,
) {
  const player = useVideoPlayer2();
  useEffect(() => {
    if (callback) {
      const subscription = player.on(event, callback);
      return () => subscription.unsubscribe();
    }
  }, [player, event, callback]);
}

export function useVideoPlayerWatcher(
  event: VideoPlayer2Event,
  defaultValueProperty?: keyof VideoPlayerManager2,
) {
  const player = useVideoPlayer2();
  useVideoPlayer2Ready();

  const [_data, setData] = useState<any>(
    defaultValueProperty ? player[defaultValueProperty] : null,
  );

  useEffect(() => {
    if (defaultValueProperty && player[defaultValueProperty]) {
      return;
    }

    // We only need to re-render the component when the event is triggered
    const subscription = player.on(event, () => {
      setData(Random.id());
    });
    return () => subscription.unsubscribe();
  }, [player, event, defaultValueProperty]);

  return _data;
}

export function useVideoPlayer2Ready() {
  const player = useVideoPlayer2();
  const [ready, setReady] = useState(player.isReady);

  useEffect(() => {
    const subscription = player.on("ready", () => {
      setReady(true);
    });

    return () => subscription.unsubscribe();
  }, [player]);

  return ready;
}

function throwError(message: string) {
  throw new Error(message);
}

export function useVideoPlayer2SetupStart(
  setup: (options: VideoPlayer2SetupStartOptions) => void,
) {
  const player = useVideoPlayer2();

  useEffect(() => {
    const video = player.element;

    if (!video) return;

    return setup({ player, video });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player]);
}
